import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { onFetchSuccess, onFetchError, onFetchStart, apiCall, dispatchError } from '../utils';
import exporter from '../exporter';
import * as api from '../../api/customer';
import { actionTypes } from '../actions';
import { PayloadAction } from '../../types/state';
import { PrestationReference, Pro, UniverseReference } from '../../types/commons';
import { BasketState } from '../common/basket';

import { CustomerRootState, ProAccountsState } from './types';

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

export const FETCH = actionTypes('PRO_ACCOUNTS_FETCH');
export const CHECK_AVAILABILITY = actionTypes('PRO_ACCOUNTS_CHECK_AVAILABILITY');
export const FETCH_PREVIOUS_PROS = actionTypes('PRO_ACCOUNTS_FETCH_PREVIOUS_PROS');

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const fetch = () => apiCall(FETCH, api.proAccounts.get);

// we really need a RootState like the one in the pro app
export const checkAvailability =
  () => async (dispatch: ThunkDispatch<CustomerRootState, void, Action>, getState: () => CustomerRootState) => {
    const {
      basket: { universe, address },
    } = getState();
    dispatch({ type: CHECK_AVAILABILITY.START });
    try {
      const result = await api.proAccounts.checkAvailability(
        universe!,
        address?.zip_code,
        address?.lat,
        address?.lng,
      );
      return dispatch({ type: CHECK_AVAILABILITY.SUCCESS, payload: result });
    } catch (error) {
      return dispatchError(dispatch, CHECK_AVAILABILITY.ERROR, error);
    }
  };

export type FetchPreviousProsArgs = {
  universe: UniverseReference;
  zipCode: string;
  referringPro?: string;
  prestations?: PrestationReference[];
  weeklyRecurrence?: BasketState['weekly_recurrence'];
  duration?: BasketState['duration'];
};

export const fetchPreviousPros = ({
  universe,
  zipCode,
  referringPro,
  prestations,
  weeklyRecurrence,
  duration,
}: FetchPreviousProsArgs) =>
  apiCall(FETCH_PREVIOUS_PROS, () =>
    api.proAccounts.getPrevious({
      universe,
      zipCode,
      referringPro,
      prestations,
      weeklyRecurrence,
      duration,
    }),
  );

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~

export const initialState: ProAccountsState = {
  isLoading: false,
  isLoaded: false,
  errors: [],
  all: [],
  availability: {
    isLoading: false,
    isLoaded: false,
    available: false,
    errors: [],
  },
  previousPros: {
    isLoading: false,
    isLoaded: false,
    all: [],
    errors: [],
  },
  referring_pro: null,
};

const onFetchPreviousProsStart = (state: ProAccountsState) => ({
  ...state,
  previousPros: onFetchStart(state.previousPros),
});

type FetchPreviousProsSuccessPayload = {
  data: Pro[];
};
const onFetchPreviousProsSuccess = (
  state: ProAccountsState,
  action: PayloadAction<FetchPreviousProsSuccessPayload>,
) => {
  const pros = action.payload.data.filter((pro: Pro) => pro.status === 'valid');

  return {
    ...state,
    previousPros: {
      ...state.previousPros,
      isLoading: false,
      isLoaded: true,
      all: pros,
    },
    referring_pro: action.payload.data.find((pro: Pro) => pro.is_referee) || null,
  };
};

type FetchPreviousProsErrorPayload = {
  errors: ProAccountsState['previousPros']['errors'];
};
const onFetchPreviousProsError = (state: ProAccountsState, action: PayloadAction<FetchPreviousProsErrorPayload>) => ({
  ...state,
  previousPros: {
    ...state.previousPros,
    errors: action.payload.errors,
    isLoading: false,
    isLoaded: false,
    all: [],
  },
  referring_pro: null,
});

const onCheckAvailabilityStart = (state: ProAccountsState) => ({
  ...state,
  availability: onFetchStart(state.availability),
});

type CheckAvailabilitySuccessPayload = {
  data: {
    available: boolean;
  };
};
const onCheckAvailabilitySuccess = (
  state: ProAccountsState,
  action: PayloadAction<CheckAvailabilitySuccessPayload>,
) => ({
  ...state,
  availability: {
    ...state.availability,
    isLoading: false,
    isLoaded: true,
    available: action.payload.data.available,
  },
});

type CheckAvailabilityErrorPayload = {
  errors: ProAccountsState['availability']['errors'];
};
const onCheckAvailabilityError = (state: ProAccountsState, action: PayloadAction<CheckAvailabilityErrorPayload>) => ({
  ...state,
  availability: {
    ...state.availability,
    errors: action.payload.errors,
    isLoading: false,
    available: false,
  },
});

const handlers = {
  [FETCH.START]: onFetchStart,
  [FETCH.SUCCESS]: onFetchSuccess,
  [FETCH.ERROR]: onFetchError,
  [FETCH_PREVIOUS_PROS.START]: onFetchPreviousProsStart,
  [FETCH_PREVIOUS_PROS.SUCCESS]: onFetchPreviousProsSuccess,
  [FETCH_PREVIOUS_PROS.ERROR]: onFetchPreviousProsError,
  [CHECK_AVAILABILITY.START]: onCheckAvailabilityStart,
  [CHECK_AVAILABILITY.SUCCESS]: onCheckAvailabilitySuccess,
  [CHECK_AVAILABILITY.ERROR]: onCheckAvailabilityError,
};

export const reducer = exporter<ProAccountsState>(initialState, handlers);
